"use client"

import Image from "next/image"

import { cn } from "@/lib/utils"

/* =============================================================================
   CreatorAvatar

   A creator is a user account, so the real picture is one they upload. This
   component is built around that: pass `src` and it renders the photo; pass
   nothing and it renders a monogram that is a designed object rather than a
   grey circle with letters in it.

   THE FALLBACK IS NOT A PLACEHOLDER. Most creators will never upload anything,
   and a column of ten identical grey discs is what made this rail read as a
   spreadsheet. So the monogram earns its own identity: the hue is derived from
   the handle, which means it is stable for that person forever and no two
   neighbours in a list collide by accident, and it wears the same duotone
   gradient and hairline ring as every other raised object in the product.

   NO STOCK PHOTOS AND NO ATHLETE PHOTOS. Putting a real face on a fictional
   creator would be a claim about a real person. Instead every account wears a
   GENERATED portrait (DiceBear notionists), seeded by the handle so it is
   stable for that person forever, drawn over the same hue-derived disc. The
   monogram sits underneath as the offline fallback. Crowd rows ("81 players")
   are not people and keep the monogram. When creator uploads exist, `src`
   still wins over all of it.
   ============================================================================= */

/** Deterministic generated portrait. Illustrated, not a real person. */
function portraitOf(handle: string): string {
  return `https://api.dicebear.com/9.x/notionists/svg?seed=${encodeURIComponent(handle)}&scale=130`
}

/** Stable 0-359 from a handle, so a creator keeps one colour for good. */
function hueOf(seed: string): number {
  let hash = 0
  for (let index = 0; index < seed.length; index += 1) {
    hash = (hash * 31 + seed.charCodeAt(index)) % 360
  }
  return hash
}

function monogramOf(name: string): string {
  const parts = name.trim().split(/\s+/)
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[1][0]).toUpperCase()
}

const SIZES = {
  xs: { box: "size-5", type: "text-[8px]" },
  sm: { box: "size-8", type: "text-[11px]" },
  md: { box: "size-10", type: "text-[13px]" },
} as const

export function CreatorAvatar({
  name,
  handle,
  src,
  portrait,
  size = "sm",
  className,
}: {
  name: string
  handle: string
  /** The creator's own picture. Everything else here is the fallback. */
  src?: string
  /** Seed for the generated portrait when it should not be the handle's. */
  portrait?: string
  size?: keyof typeof SIZES
  className?: string
}) {
  const metrics = SIZES[size]
  const shell = cn(
    "relative shrink-0 overflow-hidden rounded-full ring-1 ring-inset ring-white-200",
    metrics.box,
    className
  )

  if (src) {
    return (
      <span className={shell}>
        <Image src={src} alt="" fill sizes="40px" className="object-cover" />
      </span>
    )
  }

  // A crowd is not a person and gets no face.
  const crowd = /^\d/.test(name.trim())
  const hue = hueOf(handle)
  return (
    <span
      aria-hidden
      className={shell}
      style={{
        // Duotone off one hue: light from the top-left, the same directional
        // light the field cards use, so a disc of it sits in the same world.
        background: `linear-gradient(150deg, hsl(${hue} 62% 46%) 0%, hsl(${hue} 55% 28%) 52%, hsl(${hue} 45% 16%) 100%)`,
      }}
    >
      {/* The board banners' halftone, at a fraction of its usual weight: enough
          to keep the disc from reading as flat vector fill. */}
      <span
        className="absolute inset-0"
        style={{
          backgroundImage:
            "radial-gradient(rgba(255,255,255,0.5) 1px, transparent 1.2px)",
          backgroundSize: "5px 5px",
          opacity: 0.14,
          mixBlendMode: "overlay",
        }}
      />
      <span
        className={cn(
          "absolute inset-0 flex items-center justify-center font-open-sauce font-black tracking-tight text-white",
          metrics.type
        )}
        style={{ textShadow: "0 1px 2px hsl(0 0% 0% / 0.35)" }}
      >
        {monogramOf(name)}
      </span>
      {/* The generated portrait rides on top; if it never loads, the monogram
          underneath is the avatar. Plain img on purpose: a remote generated
          SVG is not next/image material. */}
      {!crowd && (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={portraitOf(portrait ?? handle)}
          alt=""
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover"
        />
      )}
    </span>
  )
}
